import { useEffect, useState } from "react";
import { CheckCircle2, CreditCard, ExternalLink, MessageSquareText, RefreshCw, ShieldCheck, Unplug } from "lucide-react";
import { request } from "./FunctionalApp";

type Provider = {
  provider: "STRIPE" | "SQUARE";
  available: boolean;
  mode: "SANDBOX" | "LIVE";
  connection: null | { status: "PENDING" | "CONNECTED" | "RESTRICTED"; providerAccountId: string | null; connectedAt: string | null };
};

function ProviderConnections() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  async function load() {
    const response = await request("/api/billing/providers");
    if (response.ok) setProviders((await response.json()).providers);
  }
  useEffect(() => {
    const result = new URLSearchParams(location.search).get("payment-provider");
    async function returned() {
      if (result === "stripe-returned") {
        await request("/api/billing/providers/stripe/complete", { method: "POST" });
        setMessage("Stripe account connected.");
        history.replaceState({}, "", location.pathname);
      } else if (result === "square-connected") {
        setMessage("Square account connected.");
        history.replaceState({}, "", location.pathname);
      }
      await load();
    }
    returned();
  }, []);
  async function connect(provider: Provider["provider"]) {
    setBusy(provider);
    setMessage("");
    const response = await request(`/api/billing/providers/${provider.toLowerCase()}/connect`, { method: "POST" });
    const body = await response.json();
    if (response.ok && body.url) location.assign(body.url);
    else {
      setMessage(body.error || "Unable to start account connection.");
      setBusy("");
    }
  }
  async function disconnect(provider: Provider["provider"]) {
    const name = provider === "STRIPE" ? "Stripe" : "Square";
    if (!confirm(`Disconnect ${name} from this business?`)) return;
    setBusy(provider);
    const response = await request(`/api/billing/providers/${provider.toLowerCase()}`, { method: "DELETE" });
    setMessage(response.ok ? `${name} disconnected.` : "Unable to disconnect provider.");
    setBusy("");
    await load();
  }
  return (
    <section className="func-panel payment-providers">
      <div className="func-table-head">
        <div><h2><CreditCard /> Customer payment providers</h2><p>Connect the business account that should receive appointment payments.</p></div>
        <button className="secondary" onClick={load}><RefreshCw /> Refresh</button>
      </div>
      {message ? <p className="provider-message" role="status">{message}</p> : null}
      <div className="provider-grid">
        {providers.map((item) => {
          const connected = item.connection?.status === "CONNECTED";
          const name = item.provider === "STRIPE" ? "Stripe" : "Square";
          return (
            <article className="provider-card" key={item.provider}>
              <div>
                <strong>{name}</strong>
                <span className={`provider-status ${connected ? "connected" : ""}`}>
                  {connected ? <><CheckCircle2 /> Connected</> : item.connection ? "Setup incomplete" : "Not connected"}
                </span>
              </div>
              <p>{item.mode === "SANDBOX" ? "Test mode — no real customer money will move." : "Live mode — real customer payments."}</p>
              {!item.available ? <small>TaskRoute platform setup is required before this provider can be connected.</small> : null}
              <footer>
                {connected ? (
                  <button className="secondary" disabled={busy === item.provider} onClick={() => disconnect(item.provider)}><Unplug /> Disconnect</button>
                ) : (
                  <button disabled={!item.available || Boolean(busy)} onClick={() => connect(item.provider)}><ExternalLink /> {item.connection ? "Continue setup" : `Connect ${name}`}</button>
                )}
              </footer>
            </article>
          );
        })}
      </div>
      <p className="func-admin-note"><ShieldCheck /> Card numbers and provider passwords are never stored by TaskRoute. Each provider hosts its own secure authorisation flow.</p>
    </section>
  );
}

function SmsSettings() {
  const [data, setData] = useState<any>(null);
  const [form, setForm] = useState<any>({ mode: "DISABLED", senderId: "TaskRoute", monthlySpendCapPence: 500 });
  const [secret, setSecret] = useState("");
  const [number, setNumber] = useState("");
  const [message, setMessage] = useState("");
  async function load() {
    const response = await request("/api/billing/sms");
    if (response.ok) { const next = await response.json(); setData(next); setForm(next.settings); }
  }
  useEffect(() => { load(); }, []);
  async function save() {
    setMessage("");
    const response = await request("/api/billing/sms", { method: "PUT", headers: { "content-type": "application/json" }, body: JSON.stringify({ ...form, apiKeySecret: secret || undefined }) });
    const body = await response.json().catch(() => ({}));
    setMessage(response.ok ? "SMS settings saved." : String(body.error || "Unable to save SMS settings.").replaceAll("_", " "));
    if (response.ok) { setSecret(""); await load(); }
  }
  async function test() {
    const response = await request("/api/billing/sms/test", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ recipient: number }) });
    const body = await response.json().catch(() => ({}));
    setMessage(response.ok ? `Test processed: ${body.status} (${body.segments} segment${body.segments === 1 ? "" : "s"}).` : String(body.error || "Unable to test SMS.").replaceAll("_", " "));
    await load();
  }
  if (!data) return null;
  return <section className="func-panel payment-providers">
    <div className="func-table-head"><div><h2><MessageSquareText /> Optional SMS notifications</h2><p>Email, browser and in-app notifications continue even when SMS is disabled or reaches its limit.</p></div></div>
    {message ? <p className="provider-message" role="status">{message}</p> : null}
    <div className="sms-settings-grid">
      <label>Delivery mode<select value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}>
        <option value="DISABLED">Disabled</option>
        {data.sandbox ? <option value="SANDBOX">Sandbox simulation</option> : null}
        <option value="TASKROUTE" disabled={!data.taskRouteAvailable}>TaskRoute SMS credit{!data.taskRouteAvailable ? " — coming soon" : ""}</option>
        <option value="BYO_TWILIO">Connect own Twilio</option>
      </select></label>
      <label>Sender name<input maxLength={11} value={form.senderId || ""} onChange={e => setForm({ ...form, senderId: e.target.value })} /></label>
      {form.mode === "TASKROUTE" ? <label>Maximum additional spend per month (£)<input type="number" min="0" step="1" value={(Number(form.monthlySpendCapPence || 0) / 100).toFixed(0)} onChange={e => setForm({ ...form, monthlySpendCapPence: Number(e.target.value) * 100 })} /></label> : null}
      {form.mode === "BYO_TWILIO" ? <>
        <label>Twilio Account SID<input value={form.accountSid || ""} onChange={e => setForm({ ...form, accountSid: e.target.value })} /></label>
        <label>Restricted API Key SID<input value={form.apiKeySid || ""} onChange={e => setForm({ ...form, apiKeySid: e.target.value })} /></label>
        <label>API key secret<input type="password" placeholder={form.isConfigured ? "Leave blank to keep existing secret" : "Required"} value={secret} onChange={e => setSecret(e.target.value)} /></label>
        <label>Messaging Service SID (optional)<input value={form.messagingServiceSid || ""} onChange={e => setForm({ ...form, messagingServiceSid: e.target.value })} /></label>
      </> : null}
    </div>
    <div className="sms-actions"><button onClick={save}>Save SMS settings</button><input aria-label="Test mobile number" placeholder="+447700900000" value={number} onChange={e => setNumber(e.target.value)} /><button className="secondary" disabled={form.mode === "DISABLED" || !number} onClick={test}>Send test</button></div>
    <div className="sms-usage"><span><b>{Number(data.usage.segments || 0)}</b> segments this month</span><span><b>£{(Number(data.usage.chargePence || 0) / 100).toFixed(2)}</b> additional TaskRoute SMS usage</span><span><b>{Number(data.usage.failed || 0)}</b> failed</span></div>
    <p className="func-admin-note"><ShieldCheck /> Twilio secrets are encrypted and never shown again. Branded sender names are one-way, so recipients cannot reply. TaskRoute SMS includes {form.includedSegments || 50} segments, then {form.overagePence || 15}p per segment.</p>
  </section>;
}

export function BillingWorkspace() {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState("");
  async function load() {
    setError("");
    const response = await request("/api/billing/status");
    if (!response.ok) return setError("Unable to load account information.");
    setData(await response.json());
  }
  useEffect(() => { load(); }, []);
  async function billingAction(path: string) {
    setError("");
    const response = await request(path, { method: "POST" }), body = await response.json().catch(() => ({}));
    if (response.ok && body.url) location.assign(body.url);
    else setError(String(body.error || "Billing service is unavailable.").replaceAll("_", " "));
  }
  if (!data) return <p className="func-empty">{error || "Loading account…"}</p>;
  return (
    <>
      <div className="func-heading">
        <div>
          <h1>{data.sandbox ? "Sandbox and payments" : "Billing and payments"}</h1>
          <p>{data.sandbox ? "This test group has full TaskRoute access without a subscription charge." : "TaskRoute subscription and customer payment-provider settings."}</p>
        </div>
      </div>
      <section className="func-panel">
        <div className="func-table-head">
          <div>
            <h2>{data.sandbox ? <CheckCircle2 /> : <CreditCard />} {data.sandbox ? "Active sandbox" : data.state}</h2>
            <p>{data.sandbox ? "No TaskRoute subscription payment is required." : data.trialEndsAt ? `Trial ends ${new Date(data.trialEndsAt).toLocaleDateString("en-GB")}` : "Subscription account"}</p>
          </div>
          <strong>{data.sandbox ? "ACTIVE" : data.state}</strong>
        </div>
        {data.sandbox ? <p className="func-admin-note"><ShieldCheck /> Payment-failure and reactivation demonstrations remain disabled. Connected providers use their test environments.</p> : null}
        {!data.sandbox ? <div className="billing-actions">
          {data.subscription?.isCustomer ? <button onClick={() => billingAction("/api/billing/portal")}><ExternalLink /> Manage subscription and card</button> : <button onClick={() => billingAction("/api/billing/reactivate")}><CreditCard /> Start subscription</button>}
          {!["ACTIVE", "GRACE"].includes(data.state) ? <button onClick={() => billingAction("/api/billing/reactivate")}><CreditCard /> Pay and reactivate</button> : null}
          {data.subscription?.currentPeriodEnd ? <small>Current period ends {new Date(data.subscription.currentPeriodEnd).toLocaleDateString("en-GB")}{data.subscription.cancelAtPeriodEnd ? " · cancellation scheduled" : ""}</small> : null}
          {data.provider !== "stripe" ? <p className="func-admin-note">Live TaskRoute subscription billing has not yet been enabled by the platform administrator.</p> : null}
        </div> : null}
      </section>
      <ProviderConnections />
      <SmsSettings />
    </>
  );
}
